import axios from "axios";
import { getToken, isSessionExpired, clearSession } from "./session.js";

// Base URL for your API
// Production URL
// export const BASE_URL = "https://los.mangofinance.net:8080/Morakot";
// UAT URL
export const BASE_URL = "http://47.129.56.233:8090/Morakot";


// Create axios instance with default config
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let activeSubmitRequests = 0;
const loadingSubscribers = new Set();

const notifyLoadingSubscribers = () => {
  const isLoading = activeSubmitRequests > 0;
  loadingSubscribers.forEach((subscriber) => subscriber(isLoading));
};

const isTrackableMethod = (method = "") => {
  const normalized = String(method).toLowerCase();
  return ["get", "post", "put", "patch", "delete"].includes(normalized);
};

const trackSubmitRequestStart = (config) => {
  const shouldTrack = isTrackableMethod(config?.method) && !config?.skipGlobalLoading;
  config.__trackGlobalLoading = shouldTrack;
  if (shouldTrack) {
    activeSubmitRequests += 1;
    notifyLoadingSubscribers();
  }
  return config;
};

const trackSubmitRequestEnd = (config) => {
  if (config?.__trackGlobalLoading) {
    activeSubmitRequests = Math.max(0, activeSubmitRequests - 1);
    notifyLoadingSubscribers();
  }
};

export const subscribeApiSubmitLoading = (callback) => {
  loadingSubscribers.add(callback);
  callback(activeSubmitRequests > 0);
  return () => {
    loadingSubscribers.delete(callback);
  };
};

// Request interceptor to add token to every request
apiClient.interceptors.request.use(
  (config) => {
    // Check if token is expired
    if (isSessionExpired()) {
      clearSession();
      // Optionally redirect to login
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(new Error("Session expired"));
    }

    // Get token and add to headers
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return trackSubmitRequestStart(config);
  },
  (error) => {
    trackSubmitRequestEnd(error?.config);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    trackSubmitRequestEnd(response?.config);
    return response;
  },
  (error) => {
    trackSubmitRequestEnd(error?.config);
    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      clearSession();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

/**
 * API Helper Functions
 * 
 * Usage Examples:
 * 
 * // GET request
 * const data = await api.get("/credit/lead/source/list");
 * 
 * // POST request
 * const result = await api.post("/credit/application/submit", {
 *   productInfo: {...},
 *   borrower: {...}
 * });
 * 
 * // PUT request
 * await api.put("/credit/application/update/123", updatedData);
 * 
 * // DELETE request
 * await api.delete("/credit/application/delete/123");
 * 
 * All requests automatically include the Bearer token from localStorage.
 * Token expires after 12 hours and will auto-clear if expired.
 */
export const api = {
  // GET request
  get: async (endpoint, config = {}) => {
    const response = await apiClient.get(endpoint, config);
    return response.data;
  },

  // POST request
  post: async (endpoint, data, config = {}) => {
    const response = await apiClient.post(endpoint, data, config);
    return response.data;
  },

  // PUT request
  put: async (endpoint, data, config = {}) => {
    const response = await apiClient.put(endpoint, data, config);
    return response.data;
  },

  // PATCH request
  patch: async (endpoint, data, config = {}) => {
    const response = await apiClient.patch(endpoint, data, config);
    return response.data;
  },

  // DELETE request
  delete: async (endpoint, config = {}) => {
    const response = await apiClient.delete(endpoint, config);
    return response.data;
  },
};

// Export the axios instance for advanced usage
export default apiClient;

